import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type { AdminCategory } from "@shopexperts/legacy-api";

const QUERY_KEY = ["admin", "service-categories"] as const;

export function useCategoriesManager() {
    const api = useApi();
    const queryClient = useQueryClient();
    const toaster = useToaster();

    const invalidateTree = () => {
        queryClient.invalidateQueries({ queryKey: [...QUERY_KEY] });
    };

    const {
        data: categories,
        isLoading,
        error,
        refetch,
    } = useQuery<AdminCategory[]>({
        queryKey: [...QUERY_KEY],
        queryFn: async () => {
            return await api.admin.serviceCategories.listTree();
        },
        staleTime: 2 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
    });

    const createCategory = useMutation({
        mutationFn: async ({ name, groupName }: { name: string; groupName: string | null }) => {
            return await api.admin.serviceCategories.createCategory({
                body: { name, group_name: groupName },
            });
        },
        onSuccess: () => {
            toaster.create({ title: "Category created successfully", type: "success" });
            invalidateTree();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to create category: ${error.message}`, type: "error" });
        },
    });

    const updateCategory = useMutation({
        mutationFn: async ({ id, name, groupName }: { id: number; name: string; groupName: string | null }) => {
            return await api.admin.serviceCategories.updateCategory({
                params: { categoryId: id },
                body: { name, group_name: groupName },
            });
        },
        onSuccess: () => {
            toaster.create({ title: "Category updated successfully", type: "success" });
            invalidateTree();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to update category: ${error.message}`, type: "error" });
        },
    });

    const deleteCategory = useMutation({
        mutationFn: async (id: number) => {
            return await api.admin.serviceCategories.deleteCategory({
                params: { categoryId: id },
            });
        },
        onSuccess: () => {
            toaster.create({ title: "Category deleted successfully", type: "success" });
            invalidateTree();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to delete category: ${error.message}`, type: "error" });
        },
    });

    const createSubcategory = useMutation({
        mutationFn: async ({ categoryId, name }: { categoryId: number; name: string }) => {
            return await api.admin.serviceCategories.createSubcategory({
                body: { category_id: categoryId, name },
            });
        },
        onSuccess: () => {
            toaster.create({ title: "Subcategory created successfully", type: "success" });
            invalidateTree();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to create subcategory: ${error.message}`, type: "error" });
        },
    });

    const updateSubcategory = useMutation({
        mutationFn: async ({ id, categoryId, name }: { id: number; categoryId: number; name: string }) => {
            return await api.admin.serviceCategories.updateSubcategory({
                params: { subcategoryId: id },
                body: { category_id: categoryId, name },
            });
        },
        onSuccess: () => {
            toaster.create({ title: "Subcategory updated successfully", type: "success" });
            invalidateTree();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to update subcategory: ${error.message}`, type: "error" });
        },
    });

    const deleteSubcategory = useMutation({
        mutationFn: async (id: number) => {
            return await api.admin.serviceCategories.deleteSubcategory({
                params: { subcategoryId: id },
            });
        },
        onSuccess: () => {
            toaster.create({ title: "Subcategory deleted successfully", type: "success" });
            invalidateTree();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to delete subcategory: ${error.message}`, type: "error" });
        },
    });

    return {
        categories,
        isLoading,
        error,
        refetch,
        createCategory,
        updateCategory,
        deleteCategory,
        createSubcategory,
        updateSubcategory,
        deleteSubcategory,
    };
}
